import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../hooks/useAuth'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  RotateCcw,
  Target,
  Clock,
  BookOpen,
  Play,
  Check, // added
  CheckCircle,
  XCircle,
  RefreshCw,
  Lightbulb,
  X,
} from 'lucide-react'
import { RootState, AppDispatch } from '../store'
import { flashcardService } from '../services/flashcardService'
import { getSubjectByValue } from '../constants/lgsSubjects'
import { 
  getIncorrectCardsByDeck, 
  getReviewCardsByDeck,
  saveIncorrectCard,
  saveReviewCard,
  resetReviewData,
  fetchDecks 
} from '../store/slices/flashcardSlice'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import Badge from '../components/ui/Badge'
import { Flashcard } from '../types/flashcard'

interface ReviewCard {
  id: string
  cardId: string
  deckId: string
  deckTitle: string
  question: string
  answer: string
  hint?: string
  explanation?: string
  questionImage?: string
  answerImage?: string
  tags?: string[]
  type: 'incorrect' | 'review'
  createdAt: string
}

const FlashcardReview: React.FC = () => {
  const { subject } = useParams<{ subject: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const { user } = useSelector((state: RootState) => state.auth)
  const { 
    decks, 
    reviewCards: storeReviewCards, 
    incorrectCards: storeIncorrectCards,
    reviewCardsLoading,
    incorrectCardsLoading 
  } = useSelector((state: RootState) => state.flashcard)

  const [allCards, setAllCards] = useState<ReviewCard[]>([])
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [studyMode, setStudyMode] = useState<'all' | 'incorrect' | 'review'>('all')
  const [sessionStats, setSessionStats] = useState({
    cardsStudied: 0,
    cardsCorrect: 0,
    cardsIncorrect: 0,
    startTime: new Date()
  })
  // New: selected deck for review. null => show deck groups, otherwise filter by deck
  const ALL_DECKS = '__ALL__'
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null)

  const subjectData = getSubjectByValue(subject || '')
  // NOTE: We will derive display cards from allCards to keep a uniform shape
  // previous arrays still used for mapping, but counts will be recomputed from allCards

  useEffect(() => {
    // Auth yüklenmesi tamamlanmadan işlem yapma
    if (authLoading) {
      return
    }
    
    // Kullanıcı giriş yapmamışsa ana sayfaya yönlendir
    if (!isAuthenticated) {
      navigate('/flashcards')
      return
    }
    
    if (subject && user) {
      // Clear previous review data to avoid mixing across subjects
      dispatch(resetReviewData())
      // Reset selected deck when subject changes
      setActiveDeckId(null)
      setCurrentCardIndex(0)
      loadReviewCards()
    }
  }, [subject, user, isAuthenticated, authLoading, navigate])

  useEffect(() => {
    // Combine and map cards to ReviewCard shape based on study mode
    const reviewCardsArray = Array.isArray(storeReviewCards) ? storeReviewCards : []
    const incorrectCardsArray = Array.isArray(storeIncorrectCards) ? storeIncorrectCards : []

    const deckMap = new Map(decks.map(d => [d.id, d.title]))

    const mappedReview = reviewCardsArray.map(card => ({
      id: card.id,
      cardId: card.id,
      deckId: (card as any).deckId,
      deckTitle: deckMap.get((card as any).deckId) || 'Deste',
      question: card.question,
      answer: card.answer,
      hint: card.hint,
      explanation: (card as any).explanation,
      questionImage: (card as any).questionImage,
      answerImage: (card as any).answerImage,
      tags: (card as any).tags,
      type: 'review' as const,
      createdAt: String(card.createdAt || new Date().toISOString())
    }))

    const mappedIncorrect = incorrectCardsArray.map(card => ({
      id: card.id,
      cardId: card.id,
      deckId: (card as any).deckId,
      deckTitle: deckMap.get((card as any).deckId) || 'Deste',
      question: card.question,
      answer: card.answer,
      hint: card.hint,
      explanation: (card as any).explanation,
      questionImage: (card as any).questionImage,
      answerImage: (card as any).answerImage,
      tags: (card as any).tags,
      type: 'incorrect' as const,
      createdAt: String(card.createdAt || new Date().toISOString())
    }))

    const combined = [...mappedReview, ...mappedIncorrect]
    setAllCards(combined)
  }, [storeReviewCards, storeIncorrectCards, decks])

  // Group cards by deck to present deck-based repeat lists
  const groupedDecks = React.useMemo(() => {
    const map = new Map<string, { deckId: string; deckTitle: string; incorrect: number; review: number; total: number }>()
    for (const c of allCards) {
      const key = c.deckId
      if (!map.has(key)) {
        map.set(key, { deckId: key, deckTitle: c.deckTitle, incorrect: 0, review: 0, total: 0 })
      }
      const entry = map.get(key)!
      if (c.type === 'incorrect') entry.incorrect += 1
      if (c.type === 'review') entry.review += 1
      entry.total += 1
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total)
  }, [allCards])

  const loadReviewCards = async () => {
    if (!subject || !user) return

    try {
      setInitialLoading(true)
      
      // Determine subject info
      const subjectInfo = getSubjectByValue(subject || '')
      if (!subjectInfo) {
        setInitialLoading(false)
        return
      }
      
      // Prefer value (e.g., 'turkce'), but also accept label (e.g., 'Türkçe') for stored decks
      let subjectDecks = decks.filter(deck => deck.subject === subjectInfo.value || deck.subject === subjectInfo.label)

      // If decks not loaded yet or none matched, fetch by value first
      if (subjectDecks.length === 0) {
        try {
          const actionValue = await dispatch(fetchDecks({ subject: subjectInfo.value }))
          let fetchedDecks: typeof decks = Array.isArray((actionValue as any).payload) ? (actionValue as any).payload : []
          subjectDecks = fetchedDecks.filter(deck => deck.subject === subjectInfo.value || deck.subject === subjectInfo.label)

          // Fallback: try fetching by label if still empty (legacy decks might store label)
          if (subjectDecks.length === 0) {
            const actionLabel = await dispatch(fetchDecks({ subject: subjectInfo.label }))
            fetchedDecks = Array.isArray((actionLabel as any).payload) ? (actionLabel as any).payload : []
            subjectDecks = fetchedDecks.filter(deck => deck.subject === subjectInfo.value || deck.subject === subjectInfo.label)
          }

          // Final fallback: fetch all decks and filter client-side
          if (subjectDecks.length === 0) {
            const actionAll = await dispatch(fetchDecks(undefined as any))
            fetchedDecks = Array.isArray((actionAll as any).payload) ? (actionAll as any).payload : []
            subjectDecks = fetchedDecks.filter(deck => deck.subject === subjectInfo.value || deck.subject === subjectInfo.label)
          }
        } catch (e) {
          console.error('Decks fetch failed for subject:', subjectInfo.value, e)
        }
      }
      
      if (subjectDecks.length === 0) {
        setInitialLoading(false)
        return
      }
      
      // Load cards for each deck using Redux thunks
      await Promise.all(subjectDecks.map(async (deck) => {
        try {
          await Promise.all([
            dispatch(getReviewCardsByDeck({ userId: user.uid, deckId: deck.id })),
            dispatch(getIncorrectCardsByDeck({ userId: user.uid, deckId: deck.id }))
          ])
        } catch (error) {
          console.error(`Error loading cards for deck ${deck.id}:`, error)
        }
      }))
    } catch (error) {
      console.error('Error loading review cards:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  const handleCardFlip = () => {
    setIsFlipped(!isFlipped)
  }

  useEffect(() => {
    setShowHint(false)
  }, [currentCardIndex])

  const displayCards = React.useMemo(() => {
    // Start from all mapped cards
    let cards = allCards
    // Apply deck filter
    if (activeDeckId && activeDeckId !== ALL_DECKS) {
      cards = cards.filter(c => c.deckId === activeDeckId)
    }
    // Apply study mode filter
    if (studyMode === 'incorrect') cards = cards.filter(c => c.type === 'incorrect')
    if (studyMode === 'review') cards = cards.filter(c => c.type === 'review')
    return cards
  }, [allCards, activeDeckId, studyMode])

  const currentCard = displayCards[currentCardIndex]
  const totalCards = displayCards.length
  const progress = totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0

  const handleCardResponse = React.useCallback(async (response: 'correct' | 'incorrect' | 'review') => {
    if (!currentCard || !user) return

    // Store current card reference before state changes
    const cardToProcess = currentCard
    const deckId = (cardToProcess as any).deckId
    const cid = (cardToProcess as any).cardId || (cardToProcess as any).id

    // Immediately update UI state for better responsiveness
    const newStats = { ...sessionStats }
    newStats.cardsStudied += 1

    if (response === 'correct') {
      newStats.cardsCorrect += 1
    } else {
      newStats.cardsIncorrect += 1
    }

    setSessionStats(newStats)
    
    // Move to next card immediately for better UX
    handleNextCard()

    // Handle Firebase updates in background without blocking UI
    if (!deckId || !cid) {
      console.warn('Kart kimliği/deckId eksik, kaydetme atlandı', { deckId, cid })
      return
    }

    // Execute Firebase operations in background
    setTimeout(async () => {
      try {
        const promises = []
        
        if (response === 'incorrect') {
          promises.push(
            dispatch(saveIncorrectCard({ deckId, cardId: cid })),
            dispatch(getIncorrectCardsByDeck({ userId: user.uid, deckId }))
          )
        }
        if (response === 'review') {
          promises.push(
            dispatch(saveReviewCard({ deckId, cardId: cid })),
            dispatch(getReviewCardsByDeck({ userId: user.uid, deckId }))
          )
        }
        
        // Execute all Firebase operations in parallel
        await Promise.all(promises)
      } catch (error) {
        console.error('Error saving card in background:', error)
        // Don't show error to user as UI has already progressed
      }
    }, 0)
  }, [currentCard, user, sessionStats, dispatch])

  const handleNextCard = () => {
    if (currentCardIndex < totalCards - 1) {
      // First reset the flip state to show question side
      setIsFlipped(false)
      
      // Delay card change to prevent showing next card's answer during flip animation
      setTimeout(() => {
        setCurrentCardIndex((prev) => prev + 1)
      }, 100) // Small delay to ensure flip animation starts properly
    } else {
      // Session completed
      handleCompleteSession()
    }
  }

  const handleCompleteSession = () => {
    const duration = Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000)
    const score = sessionStats.cardsStudied > 0 ? Math.round((sessionStats.cardsCorrect / sessionStats.cardsStudied) * 100) : 0

    const sessionResult = {
      sessionId: 'review-session',
      cardsStudied: sessionStats.cardsStudied,
      cardsCorrect: sessionStats.cardsCorrect,
      cardsIncorrect: sessionStats.cardsIncorrect,
      duration,
      score,
      xpEarned: sessionStats.cardsCorrect * 10,
      badgesEarned: []
    }

    navigate('/flashcards/session-complete/review', {
      state: { sessionResult }
    })
  }

  const getElapsedTime = () => {
    const elapsed = Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const isLoading = initialLoading || reviewCardsLoading || incorrectCardsLoading || authLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!subjectData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Ders bulunamadı
          </h2>
          <Button onClick={() => navigate('/flashcards')}>
            Flashcard'lara Dön
          </Button>
        </Card>
      </div>
    )
  }

  // New: Deck grouping page (subject-level)
  if (activeDeckId === null) {
    const totalAll = allCards.length
    if (totalAll === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <div className={`w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${subjectData.color} flex items-center justify-center text-white text-4xl shadow-lg`}>
                {subjectData.icon}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {subjectData.label} - Tekrar Kartları
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                Bu derste henüz tekrar edilecek kart bulunmuyor. Çalışma yaptıkça yanlış ve tekrar kartlarınız burada görünecek.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/flashcards')} variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Flashcard'lara Dön
                </Button>
                <Button onClick={() => navigate(`/flashcards/study`)} className="bg-gradient-to-r from-blue-500 to-purple-600">
                  <Play className="w-4 h-4 mr-2" />
                  Çalışmaya Başla
                </Button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 md:py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  {subjectData.label} - Tekrar Kartları (Deste Grupları)
                </h1>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{totalAll} kart</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/flashcards')}>
                  <ArrowLeft size={16} className="mr-1" /> Geri
                </Button>
                <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-600" onClick={() => setActiveDeckId(ALL_DECKS)}>
                  <Play size={16} className="mr-1" /> Hepsini Çalış
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {groupedDecks.map(group => (
            <Card key={group.deckId} className="p-5 bg-white/90 dark:bg-gray-800/90 border-2 border-blue-200/60 dark:border-blue-800/60 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{group.deckTitle}</h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 flex gap-3">
                    <span className="inline-flex items-center gap-1"><XCircle size={14} className="text-red-500" /> {group.incorrect} yanlış</span>
                    <span className="inline-flex items-center gap-1"><RotateCcw size={14} className="text-yellow-500" /> {group.review} tekrar</span>
                    <span className="inline-flex items-center gap-1"><BookOpen size={14} className="text-blue-500" /> {group.total} toplam</span>
                  </div>
                </div>
                <Button onClick={() => { setActiveDeckId(group.deckId); setCurrentCardIndex(0); }} className="bg-gradient-to-r from-blue-500 to-purple-600">
                  <Play size={16} className="mr-1" /> Çalış
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  // From here on, deck-specific (or all) study view

  if (totalCards === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className={`w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${subjectData.color} flex items-center justify-center text-white text-4xl shadow-lg`}>
              {subjectData.icon}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {subjectData.label} - Tekrar Kartları
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Bu seçilen destede henüz tekrar edilecek kart bulunmuyor. Başka bir deste seçebilir veya çalışmaya devam edebilirsiniz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => setActiveDeckId(null)} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Deste Gruplarına Dön
              </Button>
              <Button onClick={() => navigate(`/flashcards/study`)} className="bg-gradient-to-r from-blue-500 to-purple-600">
                <Play className="w-4 h-4 mr-2" />
                Çalışmaya Başla
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-3 md:gap-4 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveDeckId(null)}
                className="bg-white/80 dark:bg-gray-700/80 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <ArrowLeft size={16} className="mr-1 md:mr-2" />
                <span className="hidden sm:inline">Gruplar</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompleteSession}
                className="bg-orange-500/80 hover:bg-orange-600 dark:bg-orange-600/80 dark:hover:bg-orange-700 text-white border-orange-500 dark:border-orange-600"
              >
                <Check size={16} className="mr-1 md:mr-2" />
                <span className="hidden sm:inline">Bitir</span>
              </Button>
              <div className="flex-1 sm:flex-none">
                <h1 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {subjectData.label} - Tekrar Kartları {activeDeckId && activeDeckId !== ALL_DECKS ? `· ${displayCards[0]?.deckTitle || ''}` : '· Tümü'}
                </h1>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  {currentCardIndex + 1} / {totalCards} kart
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4 w-full sm:w-auto justify-between sm:justify-end">
              <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Clock size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline">{getElapsedTime()}</span>
                  <span className="sm:hidden">{getElapsedTime().split(':')[0]}m</span>
                </div>
                <div className="flex items-center gap-1">
                  <Target size={14} className="md:w-4 md:h-4" />
                  <span className="hidden md:inline">{sessionStats.cardsStudied || 0} çalışıldı</span>
                  <span className="md:hidden">{sessionStats.cardsStudied || 0}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3 md:mt-4">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 md:h-3">
              <motion.div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 md:h-3 rounded-full transition-all duration-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Study Mode Selector */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap gap-2 justify-center">
          {(() => {
            const totalDisplay = displayCards.length
            const incorrectCount = displayCards.filter(c => c.type === 'incorrect').length
            const reviewCount = displayCards.filter(c => c.type === 'review').length
            return (
              <>
                <button
                  onClick={() => setStudyMode('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    studyMode === 'all'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                      : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <BookOpen size={16} />
                    <span>Tümü ({totalDisplay})</span>
                  </div>
                </button>
                <button
                  onClick={() => setStudyMode('incorrect')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    studyMode === 'incorrect'
                      ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg'
                      : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <XCircle size={16} />
                    <span>Yanlışlar ({incorrectCount})</span>
                  </div>
                </button>
                <button
                  onClick={() => setStudyMode('review')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    studyMode === 'review'
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg'
                      : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <RotateCcw size={16} />
                    <span>Tekrarlar ({reviewCount})</span>
                  </div>
                </button>
              </>
            )
          })()}
        </div>
      </div>

      {/* Flashcard */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative">
          {currentCard ? (
            <FlashcardComponent
              card={currentCard}
              isFlipped={isFlipped}
              onFlip={handleCardFlip}
              showHint={showHint}
              onToggleHint={() => setShowHint(prev => !prev)}
            />
          ) : (
            <div className="w-full h-80 sm:h-96 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <div className="text-center">
                <div className="text-gray-500 dark:text-gray-400 mb-2 text-sm md:text-base">Kart yükleniyor...</div>
                <LoadingSpinner size="sm" />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <AnimatePresence>
          {isFlipped && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3 md:gap-4 justify-center"
            >
              <Button
                onClick={() => handleCardResponse('incorrect')}
                className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
              >
                <X size={16} className="mr-2 md:w-5 md:h-5" />
                Bilmiyorum
              </Button>
              
              <Button
                onClick={() => handleCardResponse('review')}
                className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
              >
                <RefreshCw size={16} className="mr-2 md:w-5 md:h-5" />
                Tekrar Et
              </Button>
              
              <Button
                onClick={() => handleCardResponse('correct')}
                className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white text-sm md:text-base px-4 md:px-6 py-3 md:py-4 w-full sm:flex-1 sm:max-w-[140px] font-medium will-change-transform"
              >
                <Check size={16} className="mr-2 md:w-5 md:h-5" />
                Biliyorum
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default FlashcardReview

// Flashcard UI Component (same look & feel as FlashcardStudy)
interface ReviewFlashcardProps {
  card: ReviewCard
  isFlipped: boolean
  onFlip: () => void
  showHint: boolean
  onToggleHint: () => void
}

const FlashcardComponent: React.FC<ReviewFlashcardProps> = ({
  card,
  isFlipped,
  onFlip,
  showHint,
  onToggleHint
}) => {
  const [isFlipping, setIsFlipping] = React.useState(false)

  const handleFlip = () => {
    if (isFlipping) return
    setIsFlipping(true)
    onFlip()
    setTimeout(() => setIsFlipping(false), 600)
  }

  return (
    <div className="relative w-full h-80 sm:h-96 perspective-1000">
      <div
        className={`flashcard-container relative w-full h-full transform-style-preserve-3d cursor-pointer ${
          isFlipped ? 'rotate-y-180' : ''
        } ${isFlipping ? 'flipping' : ''}`}
        onClick={handleFlip}
      >
        {/* Front Side - Question */}
        <div className="flashcard-side backface-hidden">
          <Card className="w-full h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 hover:shadow-xl transition-shadow overflow-hidden">
            <div className="flex-1 flex flex-col justify-start items-center text-center min-h-0 space-y-3 sm:space-y-4">
              <div className="mb-2 sm:mb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2 text-xs sm:text-sm">Soru</Badge>
              </div>

              <div className="text-base sm:text-lg md:text-xl font-medium text-gray-900 mb-4 sm:mb-6 leading-relaxed overflow-y-auto max-h-32 sm:max-h-40">
                {card.question || 'Soru yükleniyor...'}
              </div>

              {card.questionImage && (
                <div className="mb-4 sm:mb-6 flex-shrink-0">
                  <img
                    src={card.questionImage}
                    alt="Soru görseli"
                    className="max-w-full max-h-20 sm:max-h-24 md:max-h-32 mx-auto rounded-lg shadow-md object-contain"
                  />
                </div>
              )}

              {card.hint && (
                <div className="mt-auto pt-2 sm:pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleHint()
                    }}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm"
                  >
                    <Lightbulb size={14} className="mr-1 sm:mr-2" />
                    {showHint ? 'İpucunu Gizle' : 'İpucu Göster'}
                  </Button>

                  {showHint && (
                    <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs sm:text-sm text-yellow-800">💡 {card.hint}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-2 sm:mt-4 text-xs sm:text-sm text-gray-500 flex-shrink-0">
              Kartı çevirmek için tıklayın
            </div>
          </Card>
        </div>

        {/* Back Side - Answer */}
        <div className="flashcard-side backface-hidden">
          <Card className="w-full h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 hover:shadow-xl transition-shadow overflow-hidden">
            <div className="flex-1 flex flex-col justify-start items-center text-center min-h-0 space-y-3 sm:space-y-4">
              <div className="flex-shrink-0">
                <Badge className="bg-green-100 text-green-800 text-xs sm:text-sm">Cevap</Badge>
              </div>

              <div className="text-base sm:text-lg md:text-xl font-medium text-gray-900 leading-relaxed overflow-y-auto max-h-24 sm:max-h-32 flex-shrink-0">
                {card.answer || 'Cevap yükleniyor...'}
              </div>

              {card.answerImage && (
                <div className="flex-shrink-0">
                  <img
                    src={card.answerImage}
                    alt="Cevap görseli"
                    className="max-w-full max-h-16 sm:max-h-20 md:max-h-24 mx-auto rounded-lg shadow-md object-contain"
                  />
                </div>
              )}

              {card.explanation && (
                <div className="flex-1 w-full max-h-32 sm:max-h-40 overflow-y-auto">
                  <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg text-left">
                    <h4 className="font-medium text-blue-900 mb-2 text-xs sm:text-sm">Açıklama:</h4>
                    <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">{card.explanation}</p>
                  </div>
                </div>
              )}

              {card.tags && card.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 sm:gap-2 justify-center flex-shrink-0">
                  {card.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
